import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import TextInputComponent from '../../../../components/input/TextInputComponent';
import { Divider, SaveButton, FieldWrapper, SkillkoFieldBadge, EditedText } from '../components';
import { shouldShowEdited, isSkillkoSourcedEdit } from '../profileFormConfig';

/**
 * Personal Details section of the profile form
 * Handles: firstName, surname, email, dateOfBirth, phone
 */
const PersonalDetailsSection = ({
  mode,
  config,
  profileData,
  initialProfileData,
  errors,
  handleInputChange,
  checkFieldDisabled,
  showEmail,
  onSave,
  isEdited,
  canEdit,
}) => {
  const { sectionSave } = config;
  const showEditIndicator = shouldShowEdited(mode, 'firstName');
  const isSkillkoEdit = isSkillkoSourcedEdit(mode, profileData);
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const gridSpacing = isSmUp ? 3 : 2;

  // Generate stable keys for controlled components - only changes when disabled state changes
  const getFieldKey = (fieldName, disabled) => {
    return `${fieldName}-${!!disabled}`;
  };

  const phoneInputStyle = {
    width: '100%',
    ...(errors.phone ? { border: '1px solid #d32f2f' } : {}),
  };

  return (
    <>
      <Grid item xs={12} marginBottom={{ xs: 2, sm: 3 }}>
        <Divider text="Personal details" isFirst={true} />
      </Grid>

      <Grid container spacing={gridSpacing}>
        {/* First Name */}
        <FieldWrapper 
          showEdited={showEditIndicator} 
          isEdited={initialProfileData.firstName !== profileData.firstName}
          fieldWidth={sectionSave ? 6 : 8}
          badge={isSkillkoEdit ? <SkillkoFieldBadge /> : null}
        >
          <TextInputComponent
            key={getFieldKey('firstName', checkFieldDisabled('firstName'))}
            id="firstName"
            value={profileData.firstName}
            label="Profile first name"
            disabled={checkFieldDisabled('firstName')}
            setValue={(value) => handleInputChange('firstName', value)}
            inputLabelProps={{ shrink: true }}
            error={!!errors.firstName}
            helperText={errors.firstName || ''}
            data-profile-field="firstName"
          />
        </FieldWrapper>

        {/* Surname */}
        <FieldWrapper 
          showEdited={showEditIndicator} 
          isEdited={initialProfileData.surname !== profileData.surname}
          fieldWidth={sectionSave ? 6 : 8}
          badge={isSkillkoEdit ? <SkillkoFieldBadge /> : null}
        >
          <TextInputComponent
            key={getFieldKey('surname', checkFieldDisabled('surname'))}
            id="surname"
            value={profileData.surname}
            label="Profile surname"
            disabled={checkFieldDisabled('surname')}
            setValue={(value) => handleInputChange('surname', value)}
            inputLabelProps={{ shrink: true }}
            error={!!errors.surname}
            helperText={errors.surname || ''}
            data-profile-field="surname"
          />
        </FieldWrapper>

        {/* Email - only shown in Create and Skillko modes */}
        {showEmail && (
          <FieldWrapper 
            showEdited={showEditIndicator} 
            isEdited={false}
            fieldWidth={sectionSave ? 6 : 8}
          >
            <TextInputComponent
              key={getFieldKey('email', checkFieldDisabled('email'))}
              id="email"
              value={profileData.email}
              setValue={(value) => handleInputChange('email', value)}
              label="Email"
              disabled={checkFieldDisabled('email')}
              type="email"
              inputLabelProps={{ shrink: true }}
              error={!!errors.email}
              helperText={errors.email || ''}
              data-profile-field="email"
            />
          </FieldWrapper>
        )}

        {/* Date of Birth */}
        <FieldWrapper 
          showEdited={showEditIndicator} 
          isEdited={initialProfileData.dateOfBirth !== profileData.dateOfBirth}
          fieldWidth={sectionSave ? 6 : 8}
          badge={isSkillkoEdit ? <SkillkoFieldBadge /> : null}
        >
          <Box data-profile-field="dateOfBirth">
            <DatePicker
              key={getFieldKey('dateOfBirth', checkFieldDisabled('dateOfBirth'))}
              format="DD/MM/YYYY"
              defaultValue={null}
              label="Date of Birth"
              sx={{ width: '100%' }}
              value={profileData.dateOfBirth}
              onChange={(value) => handleInputChange('dateOfBirth', value)}
              disabled={checkFieldDisabled('dateOfBirth')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.dateOfBirth,
                  helperText: errors.dateOfBirth || '',
                  id: 'dateOfBirth',
                  name: 'dateOfBirth',
                },
              }}
            />
          </Box>
        </FieldWrapper>

        {/* Phone */}
        {sectionSave ? (
          <>
            <Grid item sm={6} xs={9}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box data-profile-field="phone" sx={{ flex: 1, minWidth: 0 }}>
                  <PhoneInput
                    key={getFieldKey('phone', checkFieldDisabled('phone'))}
                    specialLabel="Phone number"
                    country="ie"
                    inputClass={errors.phone ? 'profile-phone-input-error' : ''}
                    inputStyle={phoneInputStyle}
                    value={profileData.phone}
                    onChange={(value) => handleInputChange('phone', value)}
                    disabled={checkFieldDisabled('phone')}
                    inputProps={{
                      name: 'phone',
                      id: 'phone',
                      'aria-label': 'phone',
                    }}
                  />
                  {errors.phone && (
                    <FormHelperText error>{errors.phone}</FormHelperText>
                  )}
                </Box>
                {isSkillkoEdit && (
                  <Box sx={{ mt: 2 }}>
                    <SkillkoFieldBadge />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item container sm={6} xs={3} justifyContent="space-between">
              <Grid item>
                {initialProfileData.phone !== profileData.phone && <EditedText />}
              </Grid>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <SaveButton 
                  disabled={!isEdited} 
                  onClick={onSave} 
                  isInline={true}
                  sectionSave={sectionSave}
                  canEdit={canEdit}
                />
              </Box>
            </Grid>
            <Grid 
              item 
              container 
              xs={12} 
              justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
              sx={{ mt: { xs: 1, sm: 0 } }}
            >
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <SaveButton 
                  disabled={!isEdited} 
                  onClick={onSave}
                  sectionSave={sectionSave}
                  canEdit={canEdit}
                />
              </Box>
            </Grid>
          </>
        ) : (
          <Grid item sm={8} xs={12}>
            <Box data-profile-field="phone">
              <PhoneInput
                key={getFieldKey('phone', checkFieldDisabled('phone'))}
                specialLabel="Phone number"
                country="ie"
                inputClass={errors.phone ? 'profile-phone-input-error' : ''}
                inputStyle={phoneInputStyle}
                value={profileData.phone || ''}
                onChange={(value) => handleInputChange('phone', value)}
                disabled={checkFieldDisabled('phone')}
                inputProps={{
                  name: 'phone',
                  id: 'phone',
                  'aria-label': 'phone',
                }}
                containerStyle={checkFieldDisabled('phone') ? {
                  opacity: '0.38',
                  border: '1px solid rgba(0, 0, 0, 0.87)',
                  borderRadius: '8px',
                } : undefined}
              />
              {errors.phone && (
                <FormHelperText error>{errors.phone}</FormHelperText>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default PersonalDetailsSection;

